// src/components/desk/HighlightedSearchInput.jsx
// The SearchCommandBar keyword redesign (owner 2026-07-24): recognized filter words render as
// inline HIGHLIGHTED KEYWORDS inside the field instead of separate ×-chips.
//   Phase 1 (here): the TYPED side — the parent passes the compiler's active tokens (each
//     carrying a {start,end} span in the raw text) and the mirror tints those ranges.
//   Phase 2 (IntakeTab `searchKeywords`): the MODEL side — filters set from the FacetRail are
//     PROJECTED into the same field as keywords rendered beside this input. Nothing ever
//     reverse-compiles filters back into the text (handoff invariant 10a; memory
//     `search-bar-keyword-projection`).
// Both surfaces must look identical — that is what makes them read as one field — so the
// styling lives HERE as KEYWORD_CLASS and the projection imports it. Change it in one place.
//
// Technique: a transparent <input> (which keeps ALL native behaviour — caret, selection,
// Georgian IME, scroll) sits over a mirror layer that paints the same text and tints the
// recognized character ranges. Highlight is COLOR + UNDERLINE only — never weight or style,
// which would change glyph width and drift the mirror out of alignment with the input.
import { useMemo, useRef } from 'react'

// The keyword look, shared by the mirror (typed keywords) and the projection (model
// keywords). COLOR + BACKGROUND + UNDERLINE only — inside the mirror, weight/padding would
// change glyph width and drift the painted text out of alignment with the real input, so
// anything that alters metrics is banned even though the projected side could afford it.
export const KEYWORD_CLASS = 'text-foreground bg-primary/20 rounded-[2px] underline decoration-primary/80 underline-offset-2'

// Split `text` into runs [{text, hl}] from the tokens' [start,end) spans (possibly
// non-contiguous / unordered). The compiler consumes spans so overlaps shouldn't occur,
// but we sort + clamp + skip-overlap defensively so a bad span can never crash the bar.
function buildSegments(text, tokens) {
  const s = String(text || '')
  if (!s) return []
  const spans = (tokens || [])
    .filter(t => Number.isInteger(t.start) && Number.isInteger(t.end) && t.end > t.start)
    .map(t => [Math.max(0, t.start), Math.min(s.length, t.end)])
    .sort((a, b) => a[0] - b[0])
  const out = []
  let i = 0
  for (const [a, b] of spans) {
    if (a < i) continue
    if (a > i) out.push({ text: s.slice(i, a), hl: false })
    out.push({ text: s.slice(a, b), hl: true })
    i = b
  }
  if (i < s.length) out.push({ text: s.slice(i), hl: false })
  return out
}

// `boxClassName` MUST carry the shared box metrics (padding, text size, line-height) so the
// mirror and the input measure text identically. `wrapperClassName` is layout for the outer
// span (e.g. flex-1 min-w-0). Everything else is forwarded to the <input>.
export function HighlightedSearchInput({ value = '', tokens = [], boxClassName = '', wrapperClassName = '', inputRef: extRef, ...inputProps }) {
  const innerRef = useRef(null)
  const inputRef = extRef || innerRef
  const mirrorRef = useRef(null)
  const segments = useMemo(() => buildSegments(value, tokens), [value, tokens])
  const syncScroll = () => { const i = inputRef.current, m = mirrorRef.current; if (i && m) m.scrollLeft = i.scrollLeft }
  return (
    <span className={`relative block ${wrapperClassName}`}>
      {/* Mirror: same box metrics as the input; single-line, scroll-synced, only the
          recognized spans tinted. aria-hidden — the input is the real control. */}
      <span ref={mirrorRef} aria-hidden
        className={`pointer-events-none absolute inset-0 overflow-hidden whitespace-pre ${boxClassName}`}>
        {value ? segments.map((seg, i) => seg.hl
          // Recognized keyword: FULL-brightness text (never dimmer than plain — text-primary
          // is a muted gray in this theme), a subtle tinted background + underline as the
          // distinguisher. Background/underline don't change glyph width, so the mirror stays
          // aligned with the input; padding/weight would NOT be safe here.
          ? <span key={i} className={KEYWORD_CLASS}>{seg.text}</span>
          : <span key={i} className="text-foreground">{seg.text}</span>) : null}
      </span>
      <input {...inputProps} ref={inputRef} value={value} onScroll={syncScroll}
        className={`relative w-full bg-transparent text-transparent caret-foreground placeholder:text-muted-foreground ${boxClassName}`} />
    </span>
  )
}
